import { z } from 'zod';

export const noteInaccuracyTypes = [
  'wrong_medication',
  'wrong_diagnosis',
  'wrong_history',
  'hallucinated_detail',
  'formatting',
  'other',
] as const;

export type NoteInaccuracyType = (typeof noteInaccuracyTypes)[number];

// Per-event allowed-fields schemas. `capture()` validates against these at the single emit
// boundary, so only declared fields ever reach PostHog — this is the PHI-safety guarantee.
// New product events are added to this registry by their own implementation chunks.
export const eventSchemas = {
  // Events already emitted in the app today, routed through this module so it is the sole emit site.
  user_logged_in: z.object({ auth_method: z.string().optional() }),
  login_failed: z.object({ failure_reason: z.string() }),
  user_logged_out: z.object({}),
  session_started: z.object({ since_last_session_minutes: z.number().optional() }),
  document_created: z.object({ document_type: z.string() }),
  document_deleted: z.object({ document_type: z.string(), count: z.number() }),
  user_source_uploaded: z.object({ source_type: z.string().nullish() }),
  user_source_resynced: z.object({ has_new_url: z.boolean() }),
  user_signed_up: z.object({ signup_date: z.string() }),
  note_generation: z.object({
    note_id: z.string(),
    is_from_template: z.boolean(),
    generation_time_ms: z.number(),
    // Request start -> first streamed content rendered on screen. Only present on streaming-preview
    // runs; absent when the streaming toggle is off (no content ever appears on screen mid-run).
    time_to_first_content_ms: z.number().optional(),
    output_char_count: z.number(),
    prompt_input_char_count: z.number(),
    targeted_edit: z.boolean(),
    // Whether live streamed editor preview was on for this run. Always known at emit. Invariant:
    // time_to_first_content_ms present implies streaming_enabled true (see docs/llm-agent/llm-agent-architecture.md).
    streaming_enabled: z.boolean(),
    model_id: z.string().optional(),
    provider: z.string().optional(),
    tokens_in: z.number().optional(),
    tokens_out: z.number().optional(),
  }),
  first_note_generated: z.object({
    note_id: z.string(),
    time_since_signup_minutes: z.number(),
  }),
  note_generation_failed: z.object({
    note_id: z.string(),
    error_type: z.enum(['abort_user', 'network', 'unknown', 'content_too_large']),
    time_to_failure_ms: z.number(),
    prompt_input_char_count: z.number(),
  }),
  // The backend refused an AI request because every quota and balance the user's tier draws from is used up.
  ai_credits_exhausted: z.object({
    source: z.enum(['chat', 'generate_text', 'user_source_resync', 'user_source_upload']),
  }),
  note_generation_reviewed: z.object({
    note_id: z.string(),
    time_since_generation_sec: z.number(),
    suggestions_accepted: z.number(),
    suggestions_rejected: z.number(),
  }),
  note_edited_by_user: z.object({
    note_id: z.string(),
    edit_duration_sec: z.number(),
    fields_changed: z.array(z.string()),
    content_chars_added: z.number(),
    content_chars_removed: z.number(),
    name_chars_added: z.number(),
    name_chars_removed: z.number(),
  }),
  app_ready: z.object({
    // app entry (LocalDataProvider mount) -> shell usable (user-perceived startup; startup stacked chart).
    time_to_usable_ms: z.number(),
    is_cold_start: z.boolean(),
    doc_count: z.number(),
  }),
  initial_sync_completed: z.object({
    // replication start -> sync latch (pure download cost; library-size chart).
    duration_ms: z.number(),
    // app entry -> shell usable (copied from this page load's app_ready so each sync event is a self-contained startup record; omitted when the latch lands before the usable flip).
    time_to_usable_ms: z.number().optional(),
    // app entry -> sync latch (user-perceived first-sync; startup stacked chart).
    time_to_synced_ms: z.number(),
    doc_count: z.number(),
  }),
  // Warm-start counterpart to initial_sync_completed: emitted once per leader page load when the latch was already set at replication start.
  delta_sync_completed: z.object({
    // app entry -> shell usable (copied from this page load's app_ready; omitted when in-sync lands before the usable flip).
    time_to_usable_ms: z.number().optional(),
    // app entry -> all collections first in-sync (server delta catch-up done; warm-start staleness window).
    time_to_synced_ms: z.number(),
  }),
  search_performed: z.object({
    latency_ms: z.number(),
    result_count: z.number(),
  }),
  note_flagged: z.object({
    note_id: z.string(),
    accurate: z.boolean(),
    inaccuracy_type: z.enum(noteInaccuracyTypes).optional(),
    free_text: z.string().optional(),
  }),
  block_flagged: z.object({
    block_id: z.string(),
    accurate: z.boolean(),
    inaccuracy_type: z.enum(noteInaccuracyTypes).optional(),
    free_text: z.string().optional(),
  }),
} satisfies Record<string, z.ZodObject>;

export type AnalyticsEventName = keyof typeof eventSchemas;

export type AnalyticsEventProperties<K extends AnalyticsEventName> = z.infer<(typeof eventSchemas)[K]>;
